'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { fadeUp, defaultViewport } from '@/lib/animations';
import type { MouseEvent, ReactNode } from 'react';
import { handleLandingAnchorClick } from '@/lib/cadrino-landing/scroll-to-section';

export function CadrinoShell({ children }: { children: ReactNode }) {
  return (
    <div className="cadrino-landing min-h-screen bg-cadrino-bg text-slate-900 antialiased selection:bg-blue-500/20">
      {children}
    </div>
  );
}

export function Container({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8', className)}>
      {children}
    </div>
  );
}

export function Section({
  id,
  className,
  children,
}: {
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className={cn('scroll-mt-24 py-16 md:py-24', className)}>
      {children}
    </section>
  );
}

export function SectionHeader({
  label,
  title,
  subtitle,
  className,
}: {
  label?: string;
  title: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <div className={cn('max-w-2xl', className)}>
      {label && (
        <p className="mb-3 text-xs font-medium uppercase tracking-wider text-cadrino-accent">
          {label}
        </p>
      )}
      <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.02em] text-slate-900">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base leading-relaxed text-cadrino-muted md:text-lg">
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function MotionBlock({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? false : 'hidden'}
      whileInView={reduced ? undefined : 'visible'}
      viewport={defaultViewport}
      variants={fadeUp}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
}

export function Card({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-slate-200 bg-cadrino-surface p-6 shadow-sm transition-[box-shadow,border-color,background-color] duration-300 ease-soft hover:shadow-lg hover:shadow-slate-900/10 hover:border-cadrino-primary/25',
        className
      )}
    >
      {children}
    </div>
  );
}

/** Alternating section backgrounds — use `muted` on every other card-grid section */
export function sectionBandClass(variant: 'default' | 'muted' = 'default') {
  return variant === 'muted'
    ? 'border-y border-slate-200/80 bg-slate-50/60'
    : undefined;
}

export const cardGridClass = 'mt-12 grid gap-5 sm:grid-cols-2';
export const cardGrid3Class = 'mt-12 grid gap-5 md:grid-cols-3';

export function ContentCard({
  className,
  highlighted,
  children,
}: {
  className?: string;
  highlighted?: boolean;
  children: ReactNode;
}) {
  return (
    <Card
      className={cn(
        'flex h-full flex-col',
        highlighted && 'border-cadrino-primary/35 ring-1 ring-cadrino-primary/15',
        className
      )}
    >
      {children}
    </Card>
  );
}

export function CardKicker({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        'text-xs font-semibold uppercase tracking-wider text-cadrino-accent',
        className
      )}
    >
      {children}
    </p>
  );
}

export function CardTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h3 className={cn('text-lg font-semibold leading-snug text-slate-900', className)}>
      {children}
    </h3>
  );
}

export function CardText({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={cn('text-sm leading-relaxed text-cadrino-muted', className)}>{children}</p>
  );
}

export function CardOutcome({ children }: { children: ReactNode }) {
  return (
    <p className="mt-4 border-t border-slate-100 pt-4 text-sm font-medium leading-relaxed text-slate-900">
      {children}
    </p>
  );
}

export function CardIconWrap({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cadrino-primary/10 text-cadrino-primary ring-1 ring-cadrino-primary/10">
      {children}
    </div>
  );
}

export function SectionActions({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('mt-8 flex flex-wrap gap-3', className)}>{children}</div>;
}

export function SectionCallout({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-cadrino-muted shadow-sm',
        className
      )}
    >
      {children}
    </p>
  );
}

export function FeatureDetail({
  label,
  children,
  accent = 'neutral',
}: {
  label: string;
  children: ReactNode;
  accent?: 'problem' | 'behavior' | 'outcome' | 'neutral';
}) {
  const borderClass = {
    problem: 'border-red-200',
    behavior: 'border-cadrino-primary/30',
    outcome: 'border-cadrino-accent/40',
    neutral: 'border-slate-200',
  }[accent];

  return (
    <div className={cn('border-l-2 pl-3', borderClass)}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-sm leading-relaxed text-cadrino-muted">{children}</p>
    </div>
  );
}

export function GridBg({ className }: { className?: string }) {
  return (
    <div
      className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}
      aria-hidden
    >
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-cadrino-bg/70 via-transparent to-cadrino-bg/70" />
    </div>
  );
}

function landingAnchorHandlers(
  href: string,
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
) {
  if (!href.startsWith('#')) {
    return onClick ? { onClick } : {};
  }
  return {
    onClick: (e: MouseEvent<HTMLAnchorElement>) => {
      handleLandingAnchorClick(e, href);
      onClick?.(e);
    },
  };
}

export function PrimaryButton({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  return (
    <a
      href={href}
      {...landingAnchorHandlers(href, onClick)}
      className={cn(
        'inline-flex h-11 items-center justify-center rounded-xl bg-cadrino-primary px-5 text-sm font-medium text-white transition-colors hover:bg-blue-600',
        className
      )}
    >
      {children}
    </a>
  );
}

export function SecondaryButton({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <a
      href={href}
      {...landingAnchorHandlers(href)}
      className={cn(
        'inline-flex h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50',
        className
      )}
    >
      {children}
    </a>
  );
}
